import React from 'react';
import { Link } from 'react-router-dom';
import CategoryBadge from './CategoryBadge';
import ImageWithFallback from './ImageWithFallback';

const normalizeCategorySlug = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-|-$/g, '');

const CATEGORY_LABEL_TO_SLUG = {
  'a la une': 'a-la-une',
  ia: 'ia',
  'business & opportunites': 'business-opportunites',
  'business et opportunites': 'business-opportunites',
  'aides & financements': 'aides-financements',
  'aides et financements': 'aides-financements',
  'bons plans': 'bons-plans',
  'marketing & seo': 'marketing-seo',
  'marketing et seo': 'marketing-seo',
  'analyses & opinions': 'analyses-opinions',
  'analyses et opinions': 'analyses-opinions',
  'actu rapide': 'actu-rapide'
};

const resolveArticleCover = (article) => {
  const directCover = String(article?.cover || '').trim();
  if (directCover) return directCover;

  const legacyImage = String(article?.image || '').trim();
  if (legacyImage) return legacyImage;

  return '';
};

const resolveCategorySlug = (article, categoryOverride) => {
  const articleCategory = normalizeCategorySlug(article?.category);
  if (articleCategory) return articleCategory;

  const overrideNormalized = normalizeCategorySlug(categoryOverride);
  if (overrideNormalized) {
    if (CATEGORY_LABEL_TO_SLUG[overrideNormalized]) return CATEGORY_LABEL_TO_SLUG[overrideNormalized];
    return overrideNormalized;
  }

  return null;
};

const ArticleCard = ({ article, variant = 'horizontal', categoryOverride }) => {
  const displayCategory = categoryOverride || article.categoryLabel || article.category;
  const coverSrc = resolveArticleCover(article);
  const categorySlug = resolveCategorySlug(article, categoryOverride);

  if (variant === 'hero') {
    return (
      <article className="group relative overflow-hidden rounded-xl border border-slate-300 bg-slate-900 shadow-sm">
        <ImageWithFallback
          src={coverSrc}
          alt={article.title}
          categorySlug={categorySlug}
          className="relative z-0 h-[360px] w-full object-cover transition duration-700 group-hover:scale-[1.03] md:h-[430px]"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 z-20 p-6">
          <CategoryBadge category={displayCategory} className="mb-3 bg-white/90 text-slate-800" />
          <Link to={`/article/${article.slug}`} className="block">
            <h1 className="lmd-editorial-title max-w-2xl text-3xl font-extrabold leading-tight text-white md:text-5xl">
              {article.title}
            </h1>
          </Link>
          <p className="mt-3 max-w-xl text-sm text-slate-200">{article.excerpt}</p>
          <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-300">
            <span>{article.author}</span>
            <span>•</span>
            <span>{article.date}</span>
            <span>•</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group border-b border-slate-100 py-3 last:border-b-0">
        <div className="mb-2 flex items-center justify-between gap-3">
          <CategoryBadge category={displayCategory} />
          <span className="text-[11px] uppercase tracking-wider text-slate-400">{article.date}</span>
        </div>
        <Link to={`/article/${article.slug}`} className="block">
          <h3 className="lmd-editorial-title text-lg font-bold leading-snug text-slate-900 transition group-hover:text-indigo-600">
            {article.title}
          </h3>
        </Link>
      </article>
    );
  }

  if (variant === 'denseCompact') {
    return (
      <article className="group border-b border-slate-100 py-2.5 last:border-b-0">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <CategoryBadge category={displayCategory} />
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {article.readTime}
          </span>
        </div>
        <Link to={`/article/${article.slug}`} className="block">
          <h3 className="lmd-editorial-title text-base font-bold leading-snug text-slate-900 transition group-hover:text-indigo-600">
            {article.title}
          </h3>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex gap-4 border-b border-slate-100 py-4 last:border-b-0">
      <ImageWithFallback
        src={coverSrc}
        alt={article.title}
        categorySlug={categorySlug}
        className="h-20 w-24 shrink-0 rounded-md object-cover md:h-24 md:w-32"
      />
      <div className="min-w-0">
        <CategoryBadge category={displayCategory} className="mb-2" />
        <Link to={`/article/${article.slug}`} className="block">
          <h3 className="lmd-editorial-title line-clamp-2 text-xl font-bold leading-snug text-slate-900 transition group-hover:text-indigo-600">
            {article.title}
          </h3>
        </Link>
        <p className="mt-1 line-clamp-2 text-sm text-slate-500">{article.excerpt}</p>
      </div>
    </article>
  );
};

export default ArticleCard;
